import { GaugeIcon, ShieldCheckIcon, TrendUpIcon, ClockCountdownIcon, LinkIcon, WarningIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { ScoreRetrospective } from "../components/ScoreRetrospective";

const factors = [
  { icon: TrendUpIcon, title: "Volume Conviction", weight: "20 pts", color: "#00E599",
    desc: "Real 24h trading volume on a log scale. High, sustained volume signals genuine market interest rather than a dead chart." },
  { icon: ShieldCheckIcon, title: "Liquidity Depth", weight: "20 pts", color: "#00E599",
    desc: "Liquidity relative to market cap. A healthy liquidity-to-mcap ratio makes a coin harder to rug and easier to exit." },
  { icon: GaugeIcon, title: "Buy Pressure", weight: "15 pts", color: "#F5A623",
    desc: "Ratio of buys vs sells over 24h. More buyers than sellers hints at accumulation and demand." },
  { icon: ClockCountdownIcon, title: "Survival", weight: "15 pts", color: "#F5A623",
    desc: "How long the pair has survived. Coins that live past the first days have cleared the fastest rug window." },
  { icon: LinkIcon, title: "Credibility", weight: "10 pts", color: "#00E599",
    desc: "Presence of a website, X/Twitter and Telegram. Verified socials correlate with more committed teams." },
  { icon: ShieldCheckIcon, title: "Safety (on-chain)", weight: "20 pts", color: "#22D3EE",
    desc: "Confirmed from RugCheck: mint & freeze authority revoked, LP locked/burned, and low holder concentration. Until this data is confirmed it shows as Unknown and the score is capped at 80 — never assumed safe." },
];

const flags = [
  { label: "High Volume", tone: "good", desc: ">$250K traded in 24h" },
  { label: "Deep Liquidity", tone: "good", desc: "Liquidity ≥ 10% of market cap" },
  { label: "Thin Liquidity", tone: "bad", desc: "Liquidity < 3% of market cap — hard to exit" },
  { label: "Buy Pressure", tone: "good", desc: "≥ 58% of trades are buys" },
  { label: "Sell Pressure", tone: "bad", desc: "≥ 58% of trades are sells" },
  { label: "Fresh Launch", tone: "warn", desc: "Launched < 24h ago — highest risk & reward" },
  { label: "Survived 30d+", tone: "good", desc: "Pair older than 30 days" },
  { label: "Verified Socials", tone: "good", desc: "Has website or social links" },
  { label: "Dumping", tone: "bad", desc: "Down 30%+ in 24h" },
  { label: "Mint Revoked", tone: "good", desc: "Mint authority renounced — supply can't be inflated" },
  { label: "Mint Authority Live", tone: "bad", desc: "Mint authority NOT revoked — dev can print more supply" },
  { label: "Freeze Authority Live", tone: "bad", desc: "Freeze authority NOT revoked — dev can freeze your tokens" },
  { label: "LP Locked", tone: "good", desc: "≥90% of liquidity locked/burned — dev can't pull it" },
  { label: "LP Unlocked", tone: "bad", desc: "Liquidity not locked — a rug vector" },
  { label: "Concentrated Holders", tone: "bad", desc: "Top holder controls ≥35% of supply" },
];

const toneCls = {
  good: "text-neon bg-neon/10 border-neon/20",
  bad: "text-danger bg-danger/10 border-danger/20",
  warn: "text-warn bg-warn/10 border-warn/20",
};

const verdicts = [
  { label: "High Confidence", range: "75-100", color: "#00E599" },
  { label: "Moderate Confidence", range: "55-74", color: "#6EE7B7" },
  { label: "Elevated Risk", range: "40-54", color: "#F5A623" },
  { label: "High Risk", range: "0-39", color: "#FF3366" },
  { label: "Insufficient Data", range: "n/a", color: "#8A8F98" },
];

export default function Methodology() {
  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-10 animate-fade-up">
      <div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">METHODOLOGY</h1>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          The <span className="text-neon font-semibold">Degen Score</span> is a transparent 0-100 rating built from
          on-chain market data (via DexScreener) and confirmed safety data (via RugCheck). It rewards the exact signals proven
          traders watch for — real volume, defensible liquidity, buy-side demand, survival, credibility and on-chain safety —
          while flagging classic rug risks. When safety can't be confirmed it is shown as <span className="text-white">Unknown</span> and
          the score is capped at 80 — a token is never assumed safe.
        </p>
      </div>

      <section className="space-y-4">
        <h2 className="font-heading font-bold text-xl">Scoring Factors</h2>
        <div className="space-y-3">
          {factors.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="border border-white/10 bg-card p-5 flex gap-4 hover:border-white/30 transition-colors">
                <div className="w-11 h-11 flex items-center justify-center border border-white/10 shrink-0" style={{ color: f.color }}>
                  <Icon size={24} weight="bold" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading font-bold">{f.title}</h3>
                    <span className="font-mono text-xs px-2 py-0.5 border border-white/10 text-muted-foreground">{f.weight}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading font-bold text-xl">Verdict Tiers</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {verdicts.map((v) => (
            <div key={v.label} className="border border-white/10 bg-card p-4 text-center">
              <div className="font-mono font-bold text-2xl" style={{ color: v.color }}>{v.range}</div>
              <div className="font-mono text-xs mt-1 tracking-wider" style={{ color: v.color }}>{v.label}</div>
            </div>
          ))}
        </div>
      </section>

      <ScoreRetrospective />

      <section className="space-y-4">
        <h2 className="font-heading font-bold text-xl">Signal Flags</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {flags.map((f) => (
            <div key={f.label} className="border border-white/10 bg-card p-4 flex items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">{f.desc}</span>
              <span className={`shrink-0 px-2 py-0.5 text-[11px] font-medium border ${toneCls[f.tone]}`}>{f.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading font-bold text-xl">Beyond the Degen Score</h2>
        <p className="text-sm text-muted-foreground max-w-2xl -mt-1">
          The Degen Score rates every coin. These complementary engines surface specific setups proven traders hunt for.
          See <Link to="/key" className="text-neon hover:underline">The Key</Link> for the full plain-English glossary.
        </p>
        <div className="space-y-3">
          {[
            { title: "Conviction (0–100)", weight: "credibility-blind", color: "#F0C000",
              desc: "Volume + Survival + Buy Pressure only — ignores website/socials. Catches real runners even when the token info looks weak. 75+ is ANSEM-tier. You can sort and filter the Radar by it." },
            { title: "Tech Score (0–100)", weight: "product signal", color: "#22D3EE",
              desc: "Rewards coins that look like a real product: a live website, real X/Telegram socials, utility keywords in the name/description, and survival. Tech plays tend to grind up instead of dumping." },
            { title: "Website Health check", weight: "live fetch", color: "#22D3EE",
              desc: "For each tech token we fetch its website server-side and classify it Live (real content), Parked (a 'domain for sale' page) or Dead (failed to load). A tech claim behind a parked/dead site is flagged." },
            { title: "Runner Score (0–100)", weight: "runner pattern", color: "#F0C000",
              desc: "Blends momentum, conviction, survival and clean structure to spot a fresh, organic survivor matching the 'today's runner' pattern early. Crossing 72 fires the 'Could Be Today's Runner' alert." },
            { title: "Theme / Meta detection", weight: "clustering", color: "#F0A020",
              desc: "Groups fresh tokens by shared keywords in their names to detect a new meta forming, then points you to the cleanest survivor of that theme." },
            { title: "LP lock / burn (RugCheck)", weight: "safety", color: "#00E599",
              desc: "Via the free RugCheck API we flag whether liquidity is locked/burned (🔒 dev can't pull it) or unlocked (🔓 rug vector). The Radar can filter to LP-safe coins only." },
          ].map((f) => (
            <div key={f.title} className="border border-white/10 bg-card p-5 flex gap-4 hover:border-white/30 transition-colors">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading font-bold" style={{ color: f.color }}>{f.title}</h3>
                  <span className="font-mono text-xs px-2 py-0.5 border border-white/10 text-muted-foreground">{f.weight}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="border border-warn/30 bg-warn/5 p-5 flex gap-3">
        <WarningIcon size={22} className="text-warn shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground leading-relaxed">
          <span className="text-warn font-semibold">Not financial advice.</span> Meme coins are extremely volatile and
          most go to zero. The Degen Score is a research aid, not a buy signal. Some signals (e.g. dev-supply behavior)
          are inferred from market data proxies. Always do your own research and never risk more than you can lose.
        </p>
      </div>
    </div>
  );
}
